import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import './Header.css'
import {Anchor, Avatar, Badge, Button, Drawer, Select, Tooltip} from 'antd';
import {BellOutlined, MenuOutlined, UserOutlined} from '@ant-design/icons';
import {iconSvg} from "../utils/image";
import {getUserData, isAuthenticated, logout, supabase} from "../utils/auth";
import {useDispatch, useSelector} from "react-redux";
import {clearUserRoles, loadUserRoles, setLang} from "../features/appSlice";
import NotificationsDrawer from "../components/NotificationsDrawer";
import {getText} from "../utils/helper";
import {logger} from "../utils/logger";

const Header = () => {
    const anchorRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const lang = useSelector((state) => state.app.lang);
    const [user, setUser] = useState(null);
    const [isAuth, setIsAuth] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [hasAdminAccess, setHasAdminAccess] = useState(false);
    const [notificationsDrawerOpen, setNotificationsDrawerOpen] = useState(false);
    const [activeNotificationsCount, setActiveNotificationsCount] = useState(0);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Используем profile и roles из Redux вместо отдельных запросов
    const userProfile = useSelector((state) => state.app.userProfile);
    const userRoles = useSelector((state) => state.app.userRoles || []);
    const loadingUserRoles = useSelector((state) => state.app.loadingUserRoles || false);

    // Загружаем количество активных уведомлений
    useEffect(() => {
        const loadActiveNotificationsCount = async () => {
            if (isAuth && user?.id) {
                try {
                    const {count, error} = await supabase
                        .from('notifications')
                        .select('*', {count: 'exact', head: true})
                        .eq('user_id', user.id)
                        .eq('status', 'active');

                    if (error) throw error;
                    setActiveNotificationsCount(count || 0);
                } catch (err) {
                    logger.error('Ошибка загрузки количества уведомлений:', err);
                    setActiveNotificationsCount(0);
                }
            } else {
                setActiveNotificationsCount(0);
            }
        };

        loadActiveNotificationsCount();

        // Подписываемся на изменения уведомлений
        if (isAuth && user?.id) {
            const channel = supabase
                .channel('notifications_changes')
                .on(
                    'postgres_changes',
                    {
                        event: '*',
                        schema: 'public',
                        table: 'notifications',
                        filter: `user_id=eq.${user.id}`
                    },
                    () => {
                        loadActiveNotificationsCount();
                    }
                )
                .subscribe();

            return () => {
                supabase.removeChannel(channel);
            };
        }
    }, [isAuth, user?.id]);

    // Используем avatar_url из Redux profile вместо отдельного запроса
    useEffect(() => {
        if (userProfile?.avatar_url) {
            setAvatarUrl(userProfile.avatar_url);
        } else {
            setAvatarUrl(null);
        }
    }, [userProfile?.avatar_url]);

    // Проверяем авторизацию при монтировании и при изменении location
    useEffect(() => {
        const checkAuth = async () => {
            const authenticated = isAuthenticated();
            setIsAuth(authenticated);
            if (authenticated) {
                const userData = getUserData();
                const userIdChanged = userData?.id !== user?.id;

                if (userIdChanged) {
                    setUser(userData);
                    // Загружаем роли через Redux (если еще не загружены и не идет загрузка)
                    if (userData?.id && userRoles.length === 0 && !loadingUserRoles) {
                        dispatch(loadUserRoles(userData.id));
                    }
                } else {
                    setUser(userData);
                }
            } else {
                setUser(null);
                setAvatarUrl(null);
                setHasAdminAccess(false);
                dispatch(clearUserRoles());
            }
        };

        checkAuth();
        // Проверяем каждые 60 секунд (для обновления при изменении в других вкладках)
        const interval = setInterval(() => {
            // Проверяем только авторизацию
            const authenticated = isAuthenticated();
            setIsAuth(authenticated);
            if (!authenticated) {
                setUser(null);
                setAvatarUrl(null);
                setHasAdminAccess(false);
                dispatch(clearUserRoles());
            }
        }, 60000);
        return () => clearInterval(interval);
    }, [location.pathname, user?.id, dispatch, userRoles.length, loadingUserRoles]);

    // Проверяем права доступа на основе ролей из Redux
    useEffect(() => {
        if (userRoles.length > 0) {
            const adminAccess = ['root', 'admin', 'moderator'].some(role => userRoles.includes(role));
            setHasAdminAccess(adminAccess);
        } else {
            setHasAdminAccess(false);
        }
    }, [userRoles]);

    // Функция для скролла к якорю (стабильная для deps)
    const scrollToAnchor = useCallback((targetId, retries = 10) => {
        const attemptScroll = () => {
            const targetElement = document.getElementById(targetId);
            if (targetElement) {
                const headerHeight = anchorRef.current?.offsetHeight || 0;
                const elementPosition = targetElement.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
                return true;
            }
            return false;
        };

        // Пытаемся найти элемент с несколькими попытками
        if (!attemptScroll() && retries > 0) {
            setTimeout(() => {
                scrollToAnchor(targetId, retries - 1);
            }, 50);
        }
    }, []);

    // Обработка якорей при загрузке страницы лендинга
    useEffect(() => {
        if (location.pathname === '/' && location.hash) {
            const targetId = location.hash.replace('#', '');
            // Увеличиваем задержку для полной загрузки DOM
            setTimeout(() => {
                scrollToAnchor(targetId);
            }, 200);
        }
    }, [location.pathname, location.hash, scrollToAnchor]);

    const handleClick = (e, link) => {
        e.preventDefault();
        // Обрабатываем разные форматы параметра link
        const href = link?.href || (typeof link === 'string' ? link : '');

        // Если это ссылка на админ-панель, переходим напрямую
        if (href === '/admin') {
            navigate('/admin');
            return;
        }

        const targetId = href.replace('#', '');

        if (targetId) {
            // Если мы не на лендинге, сначала переходим на лендинг с хешем
            if (location.pathname !== '/') {
                navigate(`/#${targetId}`);
                // Скролл произойдет после навигации через useEffect
            } else {
                // Если уже на лендинге, просто скроллим
                scrollToAnchor(targetId);
            }
        }
    };

    const handleLoginClick = async () => {
        if (isAuth) {
            // Выход из системы
            await logout();
            dispatch(clearUserRoles());
            setIsAuth(false);
            setUser(null);
            setHasAdminAccess(false);
            navigate('/');
        } else {
            // Вход в систему
            navigate('/login');
        }
    };

    // Получаем full_name из user_metadata или email как fallback
    const getUserDisplayName = () => {
        if (!user) return '';
        return user.user_metadata?.full_name ||
            user.user_metadata?.name ||
            user.email?.split('@')[0] ||
            getText('Пользователь', lang);
    };

    // Создаем массив навигационных пунктов для переиспользования
    const navigationItems = [
        {
            key: '1',
            href: '#anchor-main',
            label: getText('Главная', lang),
            title: <div className={'nad_item_wrap'}>
                <Tooltip title={getText('Главная', lang)}>
                    <span className={'iconNav'}><iconSvg.Home/></span>
                    <span className={'nad_item_text'}>{getText('Главная', lang)}</span>

                </Tooltip></div>,
        },
        {
            key: '2',
            href: '#anchor-map',
            label: getText('Карта', lang),
            title: <div className={'nad_item_wrap'}>
                <Tooltip title={getText('Карта', lang)}>
                    <span className={'iconNav'}><iconSvg.Marks/></span>
                    <span className={'nad_item_text'}>{getText('Карта', lang)}</span>

                </Tooltip></div>,

        },
        {
            key: '8',
            href: '#anchor-weather',
            label: getText('Погода', lang),
            title: <div className={'nad_item_wrap'}>
                <Tooltip title={getText('Погода', lang)}>
                    <span className={'iconNav'}><iconSvg.Cloud/></span>
                    <span className={'nad_item_text'}>{getText('Погода', lang)}</span>

                </Tooltip></div>,
        },
        {
            key: '3',
            href: '#anchor-privilege',
            label: getText('Преимущества', lang),
            title: <div className={'nad_item_wrap'}>
                <Tooltip title={getText('Преимущества', lang)}>
                    <span className={'iconNav'}><iconSvg.Plus/></span>
                    <span className={'nad_item_text'}>{getText('Преимущества', lang)}</span>

                </Tooltip></div>,

        },
        {
            key: '6',
            href: '#anchor-gallery',
            label: getText('Галерея', lang),
            title: <div className={'nad_item_wrap'}>
                <Tooltip title={getText('Галерея', lang)}>
                    <span className={'iconNav'}><iconSvg.Images/></span>
                    <span className={'nad_item_text'}>{getText('Галерея', lang)}</span>

                </Tooltip></div>,
        },
        {
            key: '7',
            href: '#anchor-application',
            label: getText('Возможности', lang),
            title: <div className={'nad_item_wrap'}>
                <Tooltip title={getText('Возможности', lang)}>
                    <span className={'iconNav'}><iconSvg.Woz/></span>
                    <span className={'nad_item_text'}>{getText('Возможности', lang)}</span>

                </Tooltip></div>,
        },
        {
            key: '4',
            href: '#anchor-news',
            label: getText('Новости', lang),
            title: <div className={'nad_item_wrap'}>
                <Tooltip title={getText('Новости', lang)}>
                    <span className={'iconNav'}><iconSvg.News/></span>
                    <span className={'nad_item_text'}>{getText('Новости', lang)}</span>

                </Tooltip></div>,
        },
        {
            key: '5',
            href: '#anchor-faq',
            label: 'FAQ',
            title: <div className={'nad_item_wrap'}>
                <Tooltip title={'FAQ'}>
                    <span className={'iconNav'}><iconSvg.FAQ/></span>
                    <span className={'nad_item_text'}> FAQ</span>

                </Tooltip></div>,
        },
        ...(hasAdminAccess ? [{
            key: '9',
            href: '/admin',
            label: getText('Админ', lang),
            title: <div className={'nad_item_wrap'}>
                <Tooltip title={getText('Админ', lang)}>
                    <span className={'iconNav'}><iconSvg.Shield/></span>
                    <span className={'nad_item_text'}>{getText('Админ', lang)}</span>

                </Tooltip></div>,
            isAdmin: true,
        }] : []),
    ];

    // Обработчик клика по пункту меню в Drawer
    const handleMobileMenuClick = (item) => {
        setMobileMenuOpen(false);
        if (item.href.startsWith('#')) {
            // Якорная ссылка - обрабатываем напрямую
            const targetId = item.href.replace('#', '');
            if (targetId) {
                // Если мы не на лендинге, сначала переходим на лендинг с хешем
                if (location.pathname !== '/') {
                    navigate(`/#${targetId}`);
                } else {
                    // Если уже на лендинге, просто скроллим
                    scrollToAnchor(targetId);
                }
            }
        } else {
            // Обычная ссылка
            navigate(item.href);
        }
    };

    return (
        <div className={`Header_wrapper`} ref={anchorRef}>
            <div className={'Header_logo'} onClick={() => navigate('/')}>
                <span className={''}><iconSvg.Logo/> </span>
                <span className={'logo_name'}> DroneKz</span>
            </div>
            {/* Бургер отдельно от логотипа — иначе клик уходит в navigate('/') */}
            <Button
                type="text"
                icon={<MenuOutlined/>}
                onClick={(e) => {
                    e.stopPropagation();
                    setMobileMenuOpen(true);
                }}
                className="Header_burger_button"
                aria-label={getText('Меню', lang)}
            />


            {/* Десктопная навигация */}
            <Anchor
                direction="horizontal"
                affix={false}
                onClick={handleClick}
                targetOffset={anchorRef.current?.offsetHeight || 0}
                getContainer={() => document.querySelector('.landingPage') || window}
                items={navigationItems.map(item => ({
                    key: item.key,
                    href: item.href,
                    title: item.isAdmin ? (
                        <span className={location.pathname === '/admin' ? 'Header_admin_active' : ''}>
                            {item.title}
                        </span>
                    ) : item.title,
                }))}
                className={'def_var_wrap'}
            />
            <div className="Header_auth_section">
                {isAuth && user && (
                    <>

                        <Tooltip title={getUserDisplayName() || getText('Личный кабинет', lang)}>
                            <span
                                className="Header_user_name"
                                onClick={() => navigate('/profile')}
                            >
                                <Avatar
                                    src={avatarUrl}
                                    size={20}
                                    icon={!avatarUrl ? <UserOutlined/> : null}
                                />
                                <span className="Header_user_name_text">{getUserDisplayName()}</span>
                            </span>
                        </Tooltip>

                        <Tooltip title={getText('Уведомления', lang)}>
                            <Badge count={activeNotificationsCount} offset={[-2, 2]}>
                                <Button
                                    size={'middle'}
                                    icon={<BellOutlined/>}
                                    onClick={async () => {
                                        // Обновляем счетчик перед открытием drawer
                                        if (user?.id) {
                                            try {
                                                const {count} = await supabase
                                                    .from('notifications')
                                                    .select('*', {count: 'exact', head: true})
                                                    .eq('user_id', user.id)
                                                    .eq('status', 'active');
                                                if (count !== null) {
                                                    setActiveNotificationsCount(count);
                                                }
                                            } catch (err) {
                                                logger.error('Ошибка обновления счетчика:', err);
                                            }
                                        }
                                        setNotificationsDrawerOpen(true);
                                    }}
                                    className="Header_notifications_button"
                                />
                            </Badge>
                        </Tooltip>
                    </>
                )}
                <Select
                    value={lang}
                    onChange={(value) => dispatch(setLang(value))}
                    className="Header_lang_select"

                    optionRender={(option) => (
                        <Tooltip title={
                            option.value === 'nameRu'
                                ? getText('Русский', lang)
                                : option.value === 'nameEn'
                                    ? getText('Английский', lang)
                                    : getText('Қазақша', lang)
                        } placement="left">
                            <span>{option.label}</span>
                        </Tooltip>
                    )}
                    options={[
                        {value: 'nameRu', label: getText('Ру', lang)},
                        {value: 'nameKz', label: getText('Қz', lang)},
                        {value: 'nameEn', label: getText('En', lang)},
                    ]}
                />
                <Button
                    type="primary"
                    onClick={handleLoginClick}
                    className="Header_login_button"
                >

                    {isAuth ?
                        <Tooltip title={getText('Выход', lang)}>
                            <div className={'Login_icons_wrap'}>
                                <span className={'Login_icons out_icon'}> <iconSvg.Login/></span>
                                <span>{getText('Выход', lang)}</span>
                            </div>
                        </Tooltip>
                        :
                        <Tooltip title={getText('Вход', lang)}>
                            <div className={'Login_icons_wrap'}>
                                <span className={'Login_icons out_icon'}> <iconSvg.Login/></span>
                                <span>{getText('Вход', lang)}</span>
                            </div>
                        </Tooltip>}
                </Button>
            </div>
            {/* Мобильное меню Drawer */}
            <Drawer
                title={getText('Меню', lang)}
                placement="left"
                size={180}
                onClose={() => setMobileMenuOpen(false)}
                open={mobileMenuOpen}
                className="Header_mobile_drawer"
                bodyStyle={{padding: 0}}

            >
                <div className="Header_mobile_menu">
                    {navigationItems.map((item) => (
                        <div
                            key={item.key}
                            className={`Header_mobile_menu_item ${item.isAdmin && location.pathname === '/admin' ? 'Header_admin_active' : ''}`}
                            onClick={() => handleMobileMenuClick(item)}
                        >
                            {item.label || item.key}
                        </div>
                    ))}
                </div>
            </Drawer>

            {isAuth && user && (
                <NotificationsDrawer
                    open={notificationsDrawerOpen}
                    onClose={() => setNotificationsDrawerOpen(false)}
                    onNotificationArchived={async () => {
                        // Обновляем счетчик после архивации
                        if (user?.id) {
                            try {
                                const {count} = await supabase
                                    .from('notifications')
                                    .select('*', {count: 'exact', head: true})
                                    .eq('user_id', user.id)
                                    .eq('status', 'active');
                                if (count !== null) {
                                    setActiveNotificationsCount(count);
                                }
                            } catch (err) {
                                logger.error('Ошибка обновления счетчика:', err);
                            }
                        }
                    }}
                />
            )}
        </div>
    );
};

export default Header;
